import { isDefined } from '@sgme/fp';

import type { BidAskPair } from '@/types/common.ts';

export const bidAskPairToString = (value: BidAskPair) => `${value.bid}/${value.ask}`;

export type ParseBidAskPairOptions = {
  allowNegative?: boolean; // true => allow negative values, default : false
  numbersFlavor?: 'float' | 'integer'; // default : 'integer'
};
const defaultOptions: Required<ParseBidAskPairOptions> = {
  allowNegative: false,
  numbersFlavor: 'integer',
};

const spaces = '\\s*';
const digits = '[0-9]+';

export const parseBidAskPair = (
  stringEncodedPair: string,
  options: ParseBidAskPairOptions = {},
): BidAskPair | undefined => {
  const { allowNegative, numbersFlavor } = { ...defaultOptions, ...options };

  const componentRegexParts = numbersFlavor === 'integer' ? [digits] : [digits, `(?:\\.${digits})?`];
  if (allowNegative) {
    componentRegexParts.unshift('-?');
  }
  componentRegexParts.unshift('(');
  componentRegexParts.push(')?');

  const regex = new RegExp(
    ['^', spaces, ...componentRegexParts, spaces, '\\/?', spaces, ...componentRegexParts, spaces, '$'].join(''),
  );

  const [, bidStr, askStr] = regex.exec(stringEncodedPair) ?? ['', undefined, undefined];

  const componentParser = numbersFlavor === 'integer' ? Number.parseInt : Number.parseFloat;
  const bidInt = isDefined(bidStr) ? componentParser(bidStr) : undefined;
  const askInt = isDefined(askStr) ? componentParser(askStr) : undefined;

  const bid = Number.isNaN(bidInt) ? undefined : bidInt;
  const ask = Number.isNaN(askInt) ? undefined : askInt;

  // best effort now
  if (isDefined(bid)) {
    if (isDefined(ask)) {
      return { bid, ask };
    }
    return { bid, ask: bid };
  }

  if (isDefined(ask)) {
    return { bid: ask, ask };
  }

  return undefined;
};
